import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Rocket, Plus, Play, Trash2, Building2, Loader2, Pencil, Search, LayoutGrid, List, Check, CircleDashed,
  AlertTriangle, Map, Hash, Mail, CalendarClock, Ban, MapPin, RefreshCw, Activity, Target, ArrowUp, ArrowDown,
  ArrowRight, MoreHorizontal, ChevronLeft, ChevronRight, Minus, ExternalLink, X,
  Archive, ArchiveRestore, Clock, Globe, Tag,
} from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { Loader } from "@/crm/ui";
import Modal from "@/crm/components/Modal";
import CampaignForm from "@/crm/components/CampaignForm";
import { cn } from "@/lib/utils";
import {
  STATUS_META, STATUS_TABS, PER_PAGE_OPTIONS, RETENTION_DAYS, decorate, computeKpis, countByStatus,
  filterCampaigns, sortCampaigns, paginate, fmtLastRun,
} from "@/crm/campaignsView";

const VIEW_KEY = "prospee_campaigns_view";
const readView = () => { try { return localStorage.getItem(VIEW_KEY) === "table" ? "table" : "cards"; } catch { return "cards"; } };

const STATUS_ICON = { running: Loader2, done: Check, draft: CircleDashed, error: AlertTriangle };
const CHIP_ICON = { dept: Map, naf: Hash, cp: Mail, query: Search, age: CalendarClock, closed: Ban, source: Globe, gtype: Tag };

// Gabarits (palette neutre du handoff « Campagnes »).
const CARD = "rounded-2xl border border-[#e3e3e3] bg-white shadow-[0_1px_2px_rgba(15,23,42,.04)]";
const BTN = "inline-flex items-center justify-center gap-2 rounded-xl border border-[#e3e3e3] bg-white font-medium text-[#303030] transition-all duration-200 ease-out hover:border-[#d0d0d0] hover:bg-[#fafafa] active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50";
const BTN_BLUE = "inline-flex items-center justify-center gap-2 rounded-xl bg-[#005bd3] font-semibold text-white shadow-[0_6px_14px_-8px_rgba(0,91,211,.9)] transition-all duration-200 ease-out hover:bg-[#00479f] active:scale-[.98] disabled:opacity-50";
const BTN_GREEN = "inline-flex items-center justify-center gap-[7px] rounded-[11px] bg-[#008060] text-[13px] font-semibold text-white transition-all duration-200 ease-out hover:bg-[#004c3f] active:scale-[.98]";
const BTN_GREEN_OFF = "inline-flex cursor-default items-center justify-center gap-[7px] rounded-[11px] bg-[#f2f4f7] text-[13px] font-semibold text-[#94a3b8]";
const ICON_BTN = "flex h-10 w-10 items-center justify-center rounded-[11px] border border-[#e3e3e3] bg-white text-[#94a3b8] transition-all duration-200 ease-out disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-[#e3e3e3] disabled:hover:bg-white disabled:hover:text-[#94a3b8]";
const SEG = "flex h-[34px] items-center gap-[7px] rounded-[9px] px-3 text-[13px] transition-all duration-150";
const SEG_ON = "bg-white font-semibold text-[#0f172a] shadow-[0_1px_2px_rgba(15,23,42,.12)]";
const SEG_OFF = "font-medium text-[#475569] hover:text-[#0f172a]";
const TABLE_COLS = "grid-cols-[44px_minmax(0,2.1fr)_118px_minmax(0,1.3fr)_minmax(120px,1.1fr)_minmax(96px,0.9fr)_116px]";
const CHECK = "flex h-[18px] w-[18px] items-center justify-center rounded-[5px] border text-white transition-colors";

const fmtInt = (n) => Number(n || 0).toLocaleString("fr-FR");

export default function Campaigns() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [view, setView] = useState(readView);
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE_OPTIONS[0]);
  const [selected, setSelected] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const load = useCallback(() => api.get("/campaigns").then(({ data }) => setItems(data)).catch(() => {}), []);
  const anyRunning = !!items?.some((c) => c.live?.status === "running" || c.status === "running");

  // Sondage toutes les 3 s pendant une extraction (barre de progression),
  // plus espacé au repos.
  useEffect(() => {
    load();
    timer.current = setInterval(load, anyRunning ? 3000 : 15000);
    return () => clearInterval(timer.current);
  }, [load, anyRunning]);

  useEffect(() => { setPage(1); }, [q, status, perPage]);
  const chooseView = (v) => { setView(v); try { localStorage.setItem(VIEW_KEY, v); } catch { /* non bloquant */ } };

  const all = useMemo(() => (items || []).map(decorate), [items]);
  const counts = useMemo(() => countByStatus(all), [all]);
  const kpis = useMemo(() => computeKpis(all), [all]);
  const list = useMemo(() => sortCampaigns(filterCampaigns(all, { q, status }), sort.key, sort.dir), [all, q, status, sort]);
  const paged = useMemo(() => paginate(list, page, perPage), [list, page, perPage]);
  const live = all.find((c) => c.running) || null;
  // Le serveur porte la règle ; la constante locale n'est qu'un repli d'affichage.
  const archiveDays = all.find((c) => c.retention_days)?.retention_days || RETENTION_DAYS;

  const run = async (id) => {
    try { await api.post(`/campaigns/${id}/run`); toast.success("Extraction lancée"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const runMany = async (ids) => {
    setBusy(true);
    let ok = 0;
    for (const id of ids) {
      try { await api.post(`/campaigns/${id}/run`); ok += 1; }
      catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    }
    setBusy(false);
    if (ok) toast.success(ok > 1 ? `${ok} extractions lancées` : "Extraction lancée");
    setSelected([]);
    load();
  };
  const archive = async (c) => {
    try {
      await api.post(`/campaigns/${c.id}/archive`);
      toast.success(`« ${c.name} » archivée — suppression automatique dans ${c.retention_days || RETENTION_DAYS} jours`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const unarchive = async (c) => {
    try {
      await api.post(`/campaigns/${c.id}/unarchive`);
      toast.success(`« ${c.name} » restaurée`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteMany = async (ids) => {
    setBusy(true);
    let ok = 0;
    for (const id of ids) {
      try { await api.delete(`/campaigns/${id}`); ok += 1; }
      catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    }
    setBusy(false);
    if (ok) toast.success(ok > 1 ? `${ok} campagnes supprimées` : "Campagne supprimée");
    setSelected((s) => s.filter((x) => !ids.includes(x)));
    setToDelete(null);
    load();
  };

  const sortBy = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const pageIds = paged.items.map((c) => c.id);
  const allOn = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const someOn = pageIds.some((id) => selected.includes(id));
  const toggleAll = () => setSelected((s) => (allOn
    ? s.filter((id) => !pageIds.includes(id))
    : [...s, ...pageIds.filter((id) => !s.includes(id))]));
  const selectedItems = all.filter((c) => selected.includes(c.id));

  if (!items) return <Loader />;

  const actions = {
    onRun: (c) => run(c.id),
    onDetail: (c) => navigate(`/campagnes/${c.id}`),
    onCompanies: (c) => navigate(`/entreprises?campaign_id=${c.id}`),
    onEdit: (c) => setEditing(c),
    onDelete: (c) => setToDelete([c.id]),
    onArchive: archive,
    onUnarchive: unarchive,
  };

  return (
    <div className="flex flex-col gap-[clamp(12px,1.6vw,18px)]" data-testid="campaigns-page">
      {/* En-tête */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-[260px]">
          <div className="flex items-center gap-1.5 text-[13px] text-[#64748b]">
            <span>Accueil</span><span className="text-[#cbd5e1]">/</span><span className="font-medium text-[#0f172a]">Campagnes</span>
          </div>
          <h1 className="mt-1.5 font-heading text-[clamp(24px,2.8vw,32px)] font-extrabold tracking-[-0.025em] text-[#0b1524]">Campagnes d'extraction</h1>
          <p className="mt-1.5 max-w-[52ch] text-sm text-[#64748b] [text-wrap:pretty]">
            Importez des entreprises depuis le registre officiel ou Google Places, suivez chaque extraction en direct.
          </p>
        </div>
        <button data-testid="new-campaign-btn" onClick={() => setShowNew(true)} className={cn(BTN_BLUE, "h-11 px-4 text-sm")}>
          <Plus size={17} /> Nouvelle campagne
        </button>
      </div>

      {/* Bandeau KPI */}
      {/* Séparateurs par `gap-px` sur fond gris : des filets entre les tuiles
          dans les deux sens, jamais en bordure — un `border-l` par tuile
          laissait un trait au bord gauche dès que la grille passait sur deux
          lignes (mobile). */}
      <div className={cn(CARD, "grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-px overflow-hidden bg-[#ebebeb]")} data-testid="campaigns-kpis">
        {[
          { key: "campaigns", icon: Rocket, label: "Campagnes", value: fmtInt(kpis.campaigns), bg: "#e8f1fd", fg: "#005bd3" },
          { key: "imported", icon: Building2, label: "Entreprises importées", value: fmtInt(kpis.imported), bg: "#e7f5f1", fg: "#008060" },
          { key: "running", icon: Activity, label: "Extraction en cours", value: fmtInt(kpis.running), bg: "#fdf3e7", fg: "#8a6116" },
          { key: "rate", icon: Target, label: "Objectif atteint (moy.)", value: `${kpis.objectifRate} %`, bg: "#f2f4f7", fg: "#475569" },
        ].map((k) => (
          <div key={k.key} data-testid={`kpi-${k.key}`} className="flex items-center gap-3 bg-white px-[18px] py-4">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]" style={{ background: k.bg, color: k.fg }}>
              <k.icon size={18} />
            </span>
            <span className="min-w-0">
              <span className="block font-mono text-lg font-semibold tracking-[-0.01em] text-[#0b1524]">{k.value}</span>
              <span className="block truncate text-xs text-[#64748b]">{k.label}</span>
            </span>
          </div>
        ))}
      </div>

      {/* Extraction en cours */}
      {live && (
        <div className={cn(CARD, "flex flex-wrap items-center gap-4 border-l-[3px] border-l-[#8a6116] px-[18px] py-4")} data-testid="campaigns-live">
          <div className="flex min-w-[210px] flex-[1_1_210px] items-center gap-2.5">
            <span className="h-[9px] w-[9px] shrink-0 rounded-full bg-[#8a6116] animate-pulse-dot" />
            <div className="min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-[.08em] text-[#8a6116]">Extraction en cours</div>
              <div className="truncate text-[15px] font-semibold text-[#0b1524]">{live.name}</div>
            </div>
          </div>
          <div className="min-w-[220px] flex-[2_1_320px]">
            <div className="mb-1.5 flex items-baseline justify-between text-xs text-[#64748b]">
              <span className="flex items-center gap-1.5"><MapPin size={13} />{live.current || "Démarrage…"}</span>
              <span className="font-mono font-medium text-[#303030]">{fmtInt(live.live?.inserted)} / {fmtInt(live.live?.objectif || live.objectif)}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-[#f2f4f7]">
              <div className="h-full rounded-full bg-gradient-to-r from-[#005bd3] to-[#5aa9ff] transition-[width] [transition-duration:600ms] ease-out"
                style={{ width: `${live.live?.objectif ? Math.min(100, Math.round(((live.live.inserted || 0) / live.live.objectif) * 100)) : live.pct}%` }} />
            </div>
          </div>
          <button onClick={() => actions.onDetail(live)} data-testid="campaigns-live-follow"
            className={cn(BTN, "h-[38px] px-3 text-[13px] font-semibold text-[#005bd3] hover:border-[#bcd8fb] hover:bg-[#e8f1fd]")}>
            Suivre
          </button>
        </div>
      )}

      {/* Barre d'outils collante */}
      <div className={cn(CARD, "sticky top-[68px] z-[5] flex flex-wrap items-center gap-3 px-3.5 py-3 shell-sm:top-[74px]")}>
        <div className="relative min-w-[200px] flex-[1_1_240px]">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="campaign-search"
            placeholder="Rechercher une campagne, un NAF, un type Google, un département…"
            className="h-[42px] w-full rounded-[10px] border border-[#d0d0d0] bg-white pl-9 pr-3 text-sm text-[#0f172a] outline-none transition-all focus:border-[#005bd3] focus:ring-[3px] focus:ring-[rgba(0,91,211,.16)]" />
        </div>
        <div className="flex flex-wrap gap-1 rounded-xl bg-[#f2f4f7] p-1" role="tablist" aria-label="Filtrer par statut">
          {STATUS_TABS.map((t) => {
            const on = status === t.key;
            return (
              <button key={t.key} role="tab" aria-selected={on} data-testid={`campaign-filter-${t.key}`} onClick={() => setStatus(t.key)}
                className={cn(SEG, on ? SEG_ON : SEG_OFF)}>
                {t.label}
                <span className={cn("rounded-full px-1.5 py-px font-mono text-[11px]", on ? "bg-[#f2f4f7] text-[#475569]" : "bg-[rgba(15,23,42,.06)] text-[#64748b]")}>
                  {counts[t.key] || 0}
                </span>
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex gap-1 rounded-xl bg-[#f2f4f7] p-1">
          {[["cards", "Cartes", LayoutGrid], ["table", "Tableau", List]].map(([key, label, Icon]) => (
            <button key={key} title={label} aria-label={`Vue ${label.toLowerCase()}`} aria-pressed={view === key}
              data-testid={`campaign-view-${key}`} onClick={() => chooseView(key)}
              className={cn("flex h-[34px] w-9 items-center justify-center rounded-[9px] transition-all duration-150", view === key ? SEG_ON : SEG_OFF)}>
              <Icon size={16} />
            </button>
          ))}
        </div>
      </div>

      {status === "archived" && (
        <div className="flex items-start gap-2.5 rounded-xl border border-[#e3e3e3] bg-[#fafafa] px-3.5 py-3 text-[13px] text-[#475569]" data-testid="campaigns-archive-note">
          <Archive size={15} className="mt-px shrink-0 text-[#94a3b8]" />
          <p className="m-0 [text-wrap:pretty]">
            Vous archivez vous-même une campagne quand elle n'a plus rien à faire dans la liste de travail : rien ne l'archive à votre place.
            Une campagne archivée est <b className="font-semibold text-[#303030]">supprimée automatiquement au bout de {archiveDays} jours</b> — les entreprises importées, elles, restent dans le CRM.
            Restaurez-la pour arrêter le compte à rebours.
          </p>
        </div>
      )}

      {list.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-[#d8dbe0] bg-white px-6 py-14 text-center" data-testid="campaigns-empty">
          <span className="flex h-[52px] w-[52px] items-center justify-center rounded-[14px] bg-[#e8f1fd] text-[#005bd3]"><Rocket size={24} /></span>
          <div className="font-heading text-[17px] font-bold text-[#0b1524]">
            {status === "archived" && !q ? "Aucune campagne archivée"
              : all.length === 0 ? "Aucune campagne pour l'instant" : "Aucune campagne ne correspond"}
          </div>
          <p className="m-0 max-w-[44ch] text-[13px] text-[#64748b]">
            {status === "archived" && !q
              ? `Archivez une campagne depuis sa carte pour la ranger ici ; elle sera supprimée après ${archiveDays} jours.`
              : all.length === 0
                ? "Créez une campagne pour lancer votre première extraction depuis les sources officielles."
                : "Modifiez la recherche ou le filtre de statut, ou lancez une nouvelle extraction depuis les sources officielles."}
          </p>
          <button onClick={() => setShowNew(true)} data-testid="empty-new-campaign-btn" className={cn(BTN_BLUE, "mt-1.5 h-[42px] px-4 text-sm")}>
            <Plus size={16} /> Nouvelle campagne
          </button>
        </div>
      ) : view === "cards" ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(340px,1fr))] items-start gap-3.5" data-testid="campaigns-cards">
          {list.map((c) => <CampaignCard key={c.id} c={c} {...actions} />)}
        </div>
      ) : (
        <CampaignTable
          rows={paged} total={list.length} sort={sort} onSort={sortBy}
          selected={selected} onToggle={toggleOne} onToggleAll={toggleAll} allOn={allOn} someOn={someOn}
          onClearSelection={() => setSelected([])}
          onBulkRun={() => runMany(selectedItems.filter((c) => !c.running).map((c) => c.id))}
          onBulkDelete={() => setToDelete(selected)}
          busy={busy}
          perPage={perPage} onPerPage={setPerPage} onPage={setPage}
          {...actions}
        />
      )}

      <div className="flex items-center gap-2 px-1 text-xs text-[#94a3b8]">
        <RefreshCw size={13} className={cn(anyRunning && "animate-spin")} />
        <span>Actualisation automatique toutes les 3 s pendant les extractions</span>
      </div>

      {showNew && <CampaignForm onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {editing && <CampaignForm campaign={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {toDelete && (
        <ConfirmDelete
          names={all.filter((c) => toDelete.includes(c.id)).map((c) => c.name)}
          busy={busy}
          onCancel={() => setToDelete(null)}
          onConfirm={() => deleteMany(toDelete)}
        />
      )}
    </div>
  );
}

// ---------- Badge de statut ----------
function StatusBadge({ statusKey, className }) {
  const m = STATUS_META[statusKey];
  const Icon = STATUS_ICON[statusKey];
  return (
    <span className={cn("inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full px-[9px] text-xs font-semibold", className)}
      style={{ background: m.bg, color: m.fg }} data-testid="campaign-status-badge">
      <Icon size={12} className={cn(statusKey === "running" && "animate-spin")} />{m.label}
    </span>
  );
}

// ---------- Pastille d'archive (avec compte à rebours) ----------
function ArchivedBadge({ c }) {
  return (
    <span data-testid={`campaign-archived-${c.id}`} title={c.purgeText || "Archivée"}
      className="inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full bg-[#f2f4f7] px-[9px] text-xs font-semibold text-[#475569]">
      <Archive size={12} />
      Archivée
      {c.purgeText && (
        <span className="flex items-center gap-1 border-l border-[#d8dbe0] pl-1.5 font-normal text-[#64748b]">
          <Clock size={11} />{c.daysBeforePurge} j
        </span>
      )}
    </span>
  );
}

// ---------- Bouton Lancer / Relancer ----------
function RunButton({ c, onRun, compact = false, testid }) {
  const label = c.running ? "En cours…" : c.statusKey === "draft" ? "Lancer l'extraction" : "Relancer";
  return (
    <button data-testid={testid} onClick={() => !c.running && onRun(c)} disabled={c.running} title={label} aria-label={label}
      className={cn(c.running ? BTN_GREEN_OFF : BTN_GREEN, compact ? "h-[34px] w-[34px] rounded-[9px]" : "h-10 flex-1")}>
      {c.running ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />}
      {!compact && label}
    </button>
  );
}

// ---------- Vue Cartes ----------
function CampaignCard({ c, onRun, onDetail, onEdit, onDelete, onArchive, onUnarchive }) {
  const m = STATUS_META[c.statusKey];
  const editable = c.statusKey === "draft";
  return (
    <div data-testid={`campaign-${c.id}`}
      className={cn(CARD, "flex flex-col overflow-hidden transition-shadow duration-200 hover:shadow-[0_8px_24px_-14px_rgba(15,23,42,.25)]")}>
      <div className="h-[3px]" style={{ background: m.rail }} />
      <div className="flex flex-1 flex-col gap-3.5 p-[18px]">
        <div className="flex items-start justify-between gap-2.5">
          <div className="min-w-0">
            <h3 className="m-0 font-heading text-[17px] font-bold tracking-[-0.01em] text-[#0b1524] [text-wrap:pretty]">{c.name}</h3>
            <div className="mt-1 text-xs text-[#94a3b8]">{c.meta || "Sans critère géographique"}</div>
          </div>
          <div className="flex flex-none flex-col items-end gap-1.5">
            <StatusBadge statusKey={c.statusKey} />
            {c.archived && <ArchivedBadge c={c} />}
          </div>
        </div>

        {c.chips.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {c.chips.map((chip) => {
              const Icon = CHIP_ICON[chip.kind] || Hash;
              return (
                <span key={chip.kind} className="inline-flex h-[26px] items-center gap-[5px] rounded-lg border border-[#ebebeb] bg-[#f7f8fa] px-[9px] text-xs text-[#344054]">
                  <Icon size={12} className="text-[#94a3b8]" />{chip.label}
                </span>
              );
            })}
          </div>
        )}

        <div>
          <div className="mb-[7px] flex items-baseline justify-between">
            <div className="flex items-baseline gap-[7px]">
              <span className="font-mono text-xl font-semibold text-[#0b1524]">{fmtInt(c.count)}</span>
              <span className="text-xs text-[#64748b]">entreprises importées</span>
            </div>
            <span className="font-mono text-xs text-[#94a3b8]">{c.pct}% de {fmtInt(c.objectif)}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-[#f2f4f7]">
            <div className="h-full rounded-full transition-[width] [transition-duration:600ms] ease-out" style={{ width: `${c.pct}%`, background: m.bar }} />
          </div>
          {c.running && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-[#8a6116]">
              <Loader2 size={13} className="animate-spin" />{c.current || "Démarrage…"}
            </div>
          )}
          {c.error && (
            <div className="mt-2.5 flex items-start gap-[7px] rounded-[10px] bg-[#fdecea] px-2.5 py-[9px] text-xs leading-[1.45] text-[#b42318]" data-testid={`campaign-error-${c.id}`}>
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />{c.error}
            </div>
          )}
        </div>

        <div className="mt-auto flex items-center gap-2 pt-1">
          <RunButton c={c} onRun={onRun} testid={`run-${c.id}`} />
          <button data-testid={`detail-campaign-${c.id}`} onClick={() => onDetail(c)} className={cn(BTN, "h-10 rounded-[11px] px-3 text-[13px]")}>Détail</button>
          {/* Modification possible tant que la campagne n'a jamais tourné :
              après extraction les critères ne décrivent plus les entreprises
              déjà importées (le backend refuse aussi côté serveur). */}
          <button data-testid={`edit-campaign-${c.id}`} onClick={() => editable && onEdit(c)} disabled={!editable}
            title={editable ? "Modifier" : "Critères figés depuis la première extraction"} aria-label="Modifier"
            className={cn(ICON_BTN, "hover:border-[#bcd8fb] hover:bg-[#e8f1fd] hover:text-[#005bd3]")}>
            <Pencil size={15} />
          </button>
          {/* Archiver / restaurer : une campagne « non lancée » n'a rien à
              archiver, elle n'a encore rien produit. */}
          {c.archived ? (
            <button data-testid={`unarchive-campaign-${c.id}`} onClick={() => onUnarchive(c)}
              title="Restaurer — arrête le compte à rebours" aria-label="Restaurer"
              className={cn(ICON_BTN, "hover:border-[#bcd8fb] hover:bg-[#e8f1fd] hover:text-[#005bd3]")}>
              <ArchiveRestore size={15} />
            </button>
          ) : (
            <button data-testid={`archive-campaign-${c.id}`} onClick={() => !editable && onArchive(c)} disabled={editable}
              title={editable ? "Rien à archiver : cette campagne n'a jamais tourné" : "Archiver"} aria-label="Archiver"
              className={cn(ICON_BTN, "hover:border-[#d0d0d0] hover:bg-[#f4f5f7] hover:text-[#475569]")}>
              <Archive size={15} />
            </button>
          )}
          <button data-testid={`delete-campaign-${c.id}`} onClick={() => onDelete(c)} title="Supprimer" aria-label="Supprimer"
            className={cn(ICON_BTN, "hover:border-[#f6c8c1] hover:bg-[#fdecea] hover:text-[#d72c0d]")}>
            <Trash2 size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Vue Tableau ----------
const COLUMNS = [
  { key: "name", label: "Campagne" },
  { key: "status", label: "Statut" },
  { key: null, label: "Critères" },
  { key: "count", label: "Importées" },
  { key: "lastRun", label: "Dernière exécution" },
  { key: null, label: "Actions", right: true },
];

function CampaignTable({
  rows, total, sort, onSort, selected, onToggle, onToggleAll, allOn, someOn, onClearSelection,
  onBulkRun, onBulkDelete, busy, perPage, onPerPage, onPage, onRun, onDetail, onCompanies, onEdit, onDelete,
  onArchive, onUnarchive,
}) {
  const [menuFor, setMenuFor] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (menuFor == null) return;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuFor(null); };
    const onKey = (e) => { if (e.key === "Escape") setMenuFor(null); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); window.removeEventListener("keydown", onKey); };
  }, [menuFor]);

  const pages = Array.from({ length: rows.pageCount }, (_, i) => i + 1);
  const n = selected.length;

  return (
    <div className={cn(CARD, "overflow-hidden")} data-testid="campaigns-table">
      {n > 0 && (
        <div className="flex flex-wrap items-center gap-2.5 border-b border-[#bcd8fb] bg-[#e8f1fd] px-4 py-[11px]" data-testid="campaign-selection-bar">
          <span className="text-[13px] font-semibold text-[#00427a]">{n} {n > 1 ? "campagnes sélectionnées" : "campagne sélectionnée"}</span>
          <div className="ml-auto flex gap-2">
            <button onClick={onBulkRun} disabled={busy} data-testid="campaign-bulk-run"
              className="flex h-[34px] items-center gap-1.5 rounded-[9px] bg-[#008060] px-3 text-[13px] font-semibold text-white transition-colors hover:bg-[#004c3f] disabled:opacity-50">
              <Play size={14} /> Lancer
            </button>
            <button onClick={onBulkDelete} disabled={busy} data-testid="campaign-bulk-delete"
              className="flex h-[34px] items-center gap-1.5 rounded-[9px] border border-[#bcd8fb] bg-white px-3 text-[13px] font-medium text-[#d72c0d] transition-colors hover:bg-[#fdecea] disabled:opacity-50">
              <Trash2 size={14} /> Supprimer
            </button>
            <button onClick={onClearSelection} data-testid="campaign-selection-clear"
              className="h-[34px] rounded-[9px] px-2.5 text-[13px] text-[#00427a] transition-colors hover:bg-white/60">Annuler</button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <div className="min-w-[880px]">
          <div className={cn("grid h-11 items-center border-b border-[#ebebeb] bg-[#fafafa] px-4", TABLE_COLS)}>
            <button onClick={onToggleAll} data-testid="campaign-select-all" title="Tout sélectionner" aria-label="Tout sélectionner"
              aria-checked={allOn ? "true" : someOn ? "mixed" : "false"} role="checkbox"
              className={cn(CHECK, allOn || someOn ? "border-[#005bd3] bg-[#005bd3]" : "border-[#d0d0d0] bg-white")}>
              {allOn ? <Check size={12} /> : someOn ? <Minus size={12} /> : null}
            </button>
            {COLUMNS.map((col) => {
              const active = !!col.key && sort.key === col.key;
              return (
                <button key={col.label} onClick={() => col.key && onSort(col.key)} disabled={!col.key}
                  data-testid={col.key ? `campaign-sort-${col.key}` : undefined}
                  aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
                  className={cn("flex items-center gap-[5px] text-[11px] font-bold uppercase tracking-[.07em] transition-colors",
                    col.right ? "justify-end" : "justify-start",
                    col.key ? "cursor-pointer hover:text-[#475569]" : "cursor-default",
                    active ? "text-[#0f172a]" : "text-[#94a3b8]")}>
                  {col.label}
                  {active && (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                </button>
              );
            })}
          </div>

          {rows.items.map((c) => {
            const on = selected.includes(c.id);
            const m = STATUS_META[c.statusKey];
            const editable = c.statusKey === "draft";
            return (
              <div key={c.id} data-testid={`campaign-row-${c.id}`} data-selected={on ? "true" : "false"}
                className={cn("grid min-h-[60px] items-center border-b border-[#f4f5f7] px-4 transition-colors duration-150", TABLE_COLS,
                  on ? "bg-[#f5f9ff]" : "hover:bg-[#fbfcfd]")}>
                <button onClick={() => onToggle(c.id)} data-testid={`campaign-select-${c.id}`} role="checkbox" aria-checked={on}
                  aria-label={`Sélectionner ${c.name}`}
                  className={cn(CHECK, on ? "border-[#005bd3] bg-[#005bd3]" : "border-[#d0d0d0] bg-white")}>
                  <Check size={12} className={on ? "opacity-100" : "opacity-0"} />
                </button>
                <div className="flex min-w-0 items-center gap-2.5 pr-3">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: m.rail }} />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-[#0f172a]">{c.name}</div>
                    <div className="font-mono text-xs text-[#94a3b8]">objectif {fmtInt(c.objectif)}</div>
                  </div>
                </div>
                <div><StatusBadge statusKey={c.statusKey} /></div>
                <div className="truncate pr-3 text-xs text-[#475569]" title={c.chips.map((x) => x.label).join(" · ")}>
                  {c.chips.map((x) => x.label).join(" · ") || "—"}
                </div>
                <div className="pr-4">
                  <div className="mb-[5px] flex items-baseline justify-between gap-2">
                    <span className="font-mono text-sm font-semibold text-[#0b1524]">{fmtInt(c.count)}</span>
                    <span className="font-mono text-[11px] text-[#94a3b8]">{c.pct}%</span>
                  </div>
                  <div className="h-[5px] overflow-hidden rounded-full bg-[#f2f4f7]">
                    <div className="h-full rounded-full transition-[width] [transition-duration:600ms] ease-out" style={{ width: `${c.pct}%`, background: m.bar }} />
                  </div>
                </div>
                <div className="text-xs text-[#64748b]">
                  {fmtLastRun(c.lastRunAt)}
                  {c.archived && (
                    <span data-testid={`campaign-archived-${c.id}`} title={c.purgeText}
                      className="mt-0.5 flex items-center gap-1 text-[11px] text-[#94a3b8]">
                      <Archive size={11} />Archivée{c.daysBeforePurge != null && ` · ${c.daysBeforePurge} j`}
                    </span>
                  )}
                </div>
                <div className="relative flex justify-end gap-1.5">
                  <RunButton c={c} onRun={onRun} compact testid={`run-${c.id}`} />
                  <button title="Détail" aria-label="Détail" data-testid={`detail-campaign-${c.id}`} onClick={() => onDetail(c)}
                    className="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-[#e3e3e3] bg-white text-[#64748b] transition-colors hover:border-[#bcd8fb] hover:bg-[#fafafa] hover:text-[#005bd3]">
                    <ArrowRight size={15} />
                  </button>
                  <button title="Plus d'actions" aria-label="Plus d'actions" aria-haspopup="menu" aria-expanded={menuFor === c.id}
                    data-testid={`campaign-more-${c.id}`} onClick={() => setMenuFor((v) => (v === c.id ? null : c.id))}
                    className="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-[#e3e3e3] bg-white text-[#94a3b8] transition-colors hover:bg-[#fafafa] hover:text-[#0f172a]">
                    <MoreHorizontal size={15} />
                  </button>
                  {menuFor === c.id && (
                    <div ref={menuRef} role="menu" data-testid={`campaign-menu-${c.id}`}
                      className="absolute right-0 top-[38px] z-20 w-[210px] rounded-xl border border-[#e3e3e3] bg-white p-1 shadow-[0_20px_44px_-18px_rgba(10,12,24,.35)] animate-pop-in">
                      <button role="menuitem" data-testid={`edit-campaign-${c.id}`} disabled={!editable}
                        onClick={() => { setMenuFor(null); onEdit(c); }}
                        title={editable ? undefined : "Critères figés depuis la première extraction"}
                        className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-[#303030] hover:bg-[#f7f8fa] disabled:cursor-not-allowed disabled:opacity-40">
                        <Pencil size={14} className="text-[#64748b]" /> Modifier
                      </button>
                      <button role="menuitem" data-testid={`companies-campaign-${c.id}`} onClick={() => { setMenuFor(null); onCompanies(c); }}
                        className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-[#303030] hover:bg-[#f7f8fa]">
                        <ExternalLink size={14} className="text-[#64748b]" /> Voir les entreprises
                      </button>
                      {c.archived ? (
                        <button role="menuitem" data-testid={`unarchive-campaign-${c.id}`} onClick={() => { setMenuFor(null); onUnarchive(c); }}
                          title="Arrête le compte à rebours de suppression"
                          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-[#303030] hover:bg-[#f7f8fa]">
                          <ArchiveRestore size={14} className="text-[#64748b]" /> Restaurer
                        </button>
                      ) : (
                        <button role="menuitem" data-testid={`archive-campaign-${c.id}`} disabled={editable}
                          onClick={() => { setMenuFor(null); onArchive(c); }}
                          title={editable ? "Rien à archiver : cette campagne n'a jamais tourné" : undefined}
                          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-[#303030] hover:bg-[#f7f8fa] disabled:cursor-not-allowed disabled:opacity-40">
                          <Archive size={14} className="text-[#64748b]" /> Archiver
                        </button>
                      )}
                      <button role="menuitem" data-testid={`delete-campaign-${c.id}`} onClick={() => { setMenuFor(null); onDelete(c); }}
                        className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-[#d72c0d] hover:bg-[#fdecea]">
                        <Trash2 size={14} /> Supprimer
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 bg-[#fafafa] px-4 py-3">
        <span className="text-xs text-[#64748b]" data-testid="campaign-range">
          {total === 0 ? "Aucun résultat" : `${rows.start + 1}–${rows.end} sur ${total} campagne${total > 1 ? "s" : ""}`}
        </span>
        <div className="ml-3 flex items-center gap-1.5">
          <span className="text-xs text-[#94a3b8]">Par page</span>
          <div className="flex gap-[3px] rounded-[9px] bg-[#f2f4f7] p-[3px]">
            {PER_PAGE_OPTIONS.map((p) => (
              <button key={p} onClick={() => onPerPage(p)} data-testid={`campaign-per-page-${p}`} aria-pressed={perPage === p}
                className={cn("h-7 min-w-[32px] rounded-[7px] px-2 font-mono text-xs transition-all", perPage === p ? SEG_ON : SEG_OFF)}>
                {p}
              </button>
            ))}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => onPage(rows.page - 1)} disabled={rows.page === 1} title="Page précédente" aria-label="Page précédente" data-testid="campaign-page-prev"
            className="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-[#e3e3e3] bg-white text-[#475569] transition-colors hover:bg-[#f4f5f7] disabled:cursor-default disabled:text-[#cbd5e1] disabled:hover:bg-white">
            <ChevronLeft size={16} />
          </button>
          {pages.map((p) => (
            <button key={p} onClick={() => onPage(p)} data-testid={`campaign-page-${p}`} aria-current={p === rows.page ? "page" : undefined}
              className={cn("h-[34px] min-w-[34px] rounded-[9px] border px-2 font-mono text-[13px] transition-colors",
                p === rows.page ? "cursor-default border-[#005bd3] bg-[#005bd3] font-semibold text-white" : "border-[#e3e3e3] bg-white text-[#475569] hover:border-[#d0d0d0]")}>
              {p}
            </button>
          ))}
          <button onClick={() => onPage(rows.page + 1)} disabled={rows.page === rows.pageCount} title="Page suivante" aria-label="Page suivante" data-testid="campaign-page-next"
            className="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-[#e3e3e3] bg-white text-[#475569] transition-colors hover:bg-[#f4f5f7] disabled:cursor-default disabled:text-[#cbd5e1] disabled:hover:bg-white">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Confirmation de suppression ----------
function ConfirmDelete({ names, busy, onCancel, onConfirm }) {
  const n = names.length;
  return (
    <Modal onClose={onCancel} size="sm" label="Supprimer la campagne" testid="confirm-delete-campaign">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fdecea] text-[#d72c0d]"><Trash2 size={18} /></span>
        <div className="min-w-0 flex-1">
          <h3 className="m-0 font-heading text-base font-bold text-[#0f172a]">
            {n > 1 ? `Supprimer ${n} campagnes ?` : "Supprimer cette campagne ?"}
          </h3>
          <p className="mt-1 text-[13px] leading-relaxed text-[#64748b]">
            {n > 1 ? "Les campagnes " : "La campagne "}
            <b className="text-[#303030]">{names.slice(0, 3).join(", ")}{n > 3 ? ` et ${n - 3} autre${n - 3 > 1 ? "s" : ""}` : ""}</b>
            {n > 1 ? " seront retirées" : " sera retirée"} de la liste. Les entreprises déjà importées restent dans le CRM.
          </p>
        </div>
        <button onClick={onCancel} aria-label="Fermer" className="-mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#94a3b8] hover:bg-[#f2f4f7]">
          <X size={16} />
        </button>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onCancel} data-testid="confirm-delete-cancel" className={cn(BTN, "h-10 rounded-[11px] px-4 text-sm")}>Annuler</button>
        <button onClick={onConfirm} disabled={busy} data-testid="confirm-delete-ok"
          className="inline-flex h-10 items-center gap-2 rounded-[11px] bg-[#d72c0d] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#b32200] disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Supprimer
        </button>
      </div>
    </Modal>
  );
}
